import {
  Button,
  Flex,
  sizes,
  Spacer,
  Text,
  TextInputField,
} from '@electricjs/arc';
import { Fragment, useEffect } from 'react';
import ThemeSwitcher from '../ThemeSwitcher';
import { NavLink, useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher';
import { TopNavMenuItem } from './types';
import { Organization } from '@/types/organizations';
import { handleSwitchOrg } from '@/pages/OrgSwitcher/utils';
import { UserOrganization } from '@/types/users';
import { handleCloseNavOnMenuItemClick } from './TopNavMenu';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const filterActiveOrganization = (
  currentOrgId: string,
  organizations: Organization[]
) => organizations.filter(org => org.id !== currentOrgId);

type MenuItemProps = {
  isAdminRole?: boolean;
  isDemoOrg?: boolean;
  showOrganizationSwitcher: boolean;
  showLanguageSwitcher: boolean;
  hasElectricAppEntitlement: boolean;
  hasElectricPermissions: boolean;
  organizationId: string;
  myOrganizations?: Organization[];
  myOrgRoles?: {
    [key: string]: UserOrganization;
  };
  isFetchingMyOrganizations?: boolean;
  isErrorMyOrganizations?: boolean;
  // Whether the caller's auth0_id maps to multiple distinct user records
  // (NERV-5621, e.g. multi-tenant isolved) - must be carried forward so
  // switching orgs from here keeps sending X-Disambiguate-Organization-Id.
  requiresOrgDisambiguationHeader?: boolean;
  t: (key: string) => string;
};

type OrgSwitcherSearchFormInputs = {
  search: string;
};

const StyledForm = styled.form`
  margin-top: 0.5rem;

  > div > div {
    margin-bottom: 0;
  }
`;

function OrgSwitcherSearchForm({ onSubmit }: { onSubmit: () => void }) {
  const { control, handleSubmit, setFocus } =
    useForm<OrgSwitcherSearchFormInputs>();
  const navigate = useNavigate();
  const { t } = useTranslation('components');

  useEffect(() => {
    setTimeout(() => {
      setFocus('search');
    }, 0);
  }, [setFocus]);

  const localOnSubmit: SubmitHandler<OrgSwitcherSearchFormInputs> = data => {
    navigate('/switcher' + (data.search ? `?search=${data.search}` : ''));
    onSubmit();
  };

  return (
    <StyledForm onSubmit={handleSubmit(localOnSubmit)}>
      <Flex mb={sizes.size_8_rem} vertical>
        <TextInputField
          id="switcher-search-input"
          name="search"
          label={t('topNavMenu.searchOrganizations')}
          placeholder={t('topNavMenu.orgNameOrId')}
          icon="search"
          iconPosition="left"
          mb={0}
          control={control}
        />
        <Text variant="legal" width="100%">
          <NavLink to="/switcher">{t('topNavMenu.orViewAll')}</NavLink>
        </Text>
      </Flex>
    </StyledForm>
  );
}

const generateMenuItems = ({
  isAdminRole = true,
  isDemoOrg = false,
  showOrganizationSwitcher = false,
  showLanguageSwitcher = false,
  hasElectricAppEntitlement,
  hasElectricPermissions = false,
  organizationId,
  myOrganizations,
  myOrgRoles,
  isFetchingMyOrganizations,
  isErrorMyOrganizations,
  requiresOrgDisambiguationHeader,
  t,
}: MenuItemProps) => {
  const filteredOrganizations = filterActiveOrganization(
    organizationId,
    myOrganizations ?? []
  );
  const shouldShowOrgQuickSwitcher =
    showOrganizationSwitcher &&
    !isFetchingMyOrganizations &&
    !isErrorMyOrganizations &&
    !!myOrgRoles &&
    filteredOrganizations.length > 0;

  const menuItems = [
    hasElectricAppEntitlement && {
      text: t('topNavMenu.electricForDesktop'),
      icon: 'bolt',
      component: (
        <NavLink
          to={window._env_?.VITE_DESKTOP_APP_DOWNLOAD_URL}
          target="_blank"
        />
      ),
    },
    {
      customItem: (
        <Fragment key="theme-switcher">
          <Spacer space="1rem" />
          <ThemeSwitcher key="theme-switcher" shouldHaveBottomDivider={false} />
        </Fragment>
      ),
    },
    showLanguageSwitcher && {
      customItem: <LanguageSwitcher key="language-switcher" />,
    },
    showOrganizationSwitcher && {
      text: t('topNavMenu.switchOrganization'),
      icon: 'arrow-left',
      /**
       * Quick Switcher logic:
       * - If the user has more than 1 org, show the quick switcher
       * - If the user has only 1 org, show the switcher link
       */
      ...(shouldShowOrgQuickSwitcher
        ? {}
        : {
            component: <NavLink to="/switcher" />,
          }),
      subNavItems: shouldShowOrgQuickSwitcher
        ? [
            {
              customItem: (
                <Flex key="switcher-header" px="8px" vertical>
                  <OrgSwitcherSearchForm
                    onSubmit={() => handleCloseNavOnMenuItemClick()}
                  />
                </Flex>
              ),
            },
            {
              customItem: (
                <Text pl={sizes.size_8_rem} variant="label-small">
                  {t('topNavMenu.yourOrganizations')}
                </Text>
              ),
            },
            ...filteredOrganizations.map((org: Organization) => ({
              customItem: (
                <Button
                  key={org.id}
                  id={`button-switch-org-${org.id}`}
                  onClick={handleSwitchOrg({
                    orgId: org.id,
                    myOrgRoles: myOrgRoles ?? {},
                    setError: () => console.error('Error switching org'),
                    requiresOrgDisambiguationHeader,
                  })}
                  variant="text"
                  intent="neutral"
                  truncateLabelAt="20rem"
                  width="calc(100% - 1rem)"
                  mx="0.5rem">
                  {org.name}
                </Button>
              ),
            })),
          ]
        : undefined,
    },
    isAdminRole &&
      isDemoOrg && {
        text: t('topNavMenu.demoActions'),
        icon: 'arrow-left',
        subNavItems: [
          {
            text: t('topNavMenu.createHcmOnboard'),
            icon: 'add-user',
            component: <NavLink to="/onboardings/hris/new" />,
          },
          {
            text: t('topNavMenu.createHcmOffboard'),
            icon: 'remove-circle',
            component: <NavLink to="/offboardings/hris/new" />,
          },
          {
            text: t('topNavMenu.createHcmGroupChange'),
            icon: 'user-group',
            component: <NavLink to="/group-changes/hris/new" />,
          },
        ],
      },
    hasElectricPermissions && {
      text: t('topNavMenu.internalPortal'),
      icon: 'wrench',
      component: <NavLink to="/internal" />,
    },
  ].filter(Boolean) as TopNavMenuItem[];

  return menuItems;
};

export default generateMenuItems;
